// game-builder/assetSetBatch.js
// 슬롯 여러 개를 **한 번에 순서대로** AI 생성하는 실행기.
//
// ⚠️ 왜 클라이언트가 순서를 돌리나: 한 장에 15초쯤 걸려서 11장이면 3분이다. 한 HTTP 요청으로
//    묶으면 프록시 타임아웃(보통 60초)에 잘린다. 한 장 = 한 요청으로 쪼개면 타임아웃도 없고,
//    **중간에 끊겨도 그때까지 만든 건 이미 자산으로 남는다.**
// ⚠️ 상태 이미지(피격·사망)는 그 적의 **기본 이미지를 참조**로 파생시킨다(image-to-image).
//    따로 생성하면 절대 같은 적이 안 나온다. 그래서 refFrom 이 가리키는 슬롯이 먼저 끝나야 한다.
import * as api from "../chat-builder/imageAssetApi.js";

/**
 * @param {object} opts
 * @param {string} opts.chatId       자산 저장소 키(봇이면 botId)
 * @param {Array}  opts.steps        ASSET_SET_STEPS 같은 레시피(순서가 규칙)
 * @param {string} opts.style        저작자가 쓴 공통 화풍 — 모든 장에 같이 들어가 화풍을 묶는다
 * @param {string} [opts.baseRef]    **세트 바깥** 참조 이미지 URL. `refFrom` 이 없는 '뿌리' 장들이
 *                                   이걸 참조해 만들어지고, 파생 장은 그 뿌리를 다시 참조한다
 *                                   → 세트 전체가 이 그림의 화풍을 물려받는다
 * @param {string} opts.folderPath   생성물이 담길 폴더
 * @param {(state)=>void} opts.onProgress  매 장마다 호출: { index, total, step, status, assetId, url, error }
 * @param {()=>boolean}   opts.isCancelled  true 를 돌려주면 다음 장부터 멈춘다
 * @returns {Promise<{done: object, failed: Array}>} done = slot→{assetId,url}
 */
export async function runAssetSetBatch({ chatId, steps, style, baseRef, folderPath = "", onProgress, isCancelled }) {
	const done = {};   // slot → { assetId, url }
	const failed = [];

	for (let i = 0; i < steps.length; i++) {
		if (isCancelled?.()) break;
		const step = steps[i];
		onProgress?.({ index: i, total: steps.length, step, status: "running" });

		// 파생 장(refFrom 있음)은 **이번 실행에서 방금 만든 부모**를 참조한다. 이미 슬롯에 있던 옛
		// 그림을 참조로 삼으면 새 화풍으로 만든 적에 옛 적의 피격 이미지가 붙어 어긋난다.
		// 뿌리 장(refFrom 없음)은 저작자가 고른 바깥 참조(baseRef)를 쓴다 — 없으면 순수 생성.
		const ref = step.refFrom ? done[step.refFrom]?.url : baseRef || undefined;

		try {
			const made = await api.aiCreateAsset(chatId, {
				prompt: [step.prompt, style && `#화풍\n${style}`].filter(Boolean).join("\n\n"),
				imageType: step.imageType,
				targetPath: step.imageType === "background" ? "images" : `${step.imageType}-images`,
				folderPath,
				referenceImage: ref,
				// 슬롯 이름을 파일명 슬러그로 — 11장이 전부 `_<timestamp>.png` 가 되면 나중에 못 찾는다.
				name: step.slot,
			});
			done[step.slot] = { assetId: made.assetId, url: made.url };
			onProgress?.({ index: i, total: steps.length, step, status: "done", ...made });
		} catch (e) {
			const message = e?.message || "생성 실패";
			failed.push({ slot: step.slot, label: step.label, message });
			onProgress?.({ index: i, total: steps.length, step, status: "error", error: message });
			// ⚠️ 한 장 실패로 전체를 멈추지 않는다 — 나머지는 만들어두는 편이 낫다.
			//    단, 이 장을 참조하기로 한 뒷 장들은 참조 없이(기본 생성으로) 넘어간다.
		}
	}

	return { done, failed };
}
